import type { Types } from '../types';

/**
 * TREN manual registration private-tag storage (translation-only v1).
 *
 * We store JSON strings inside private tags on *each instance* so that:
 * - tags survive export/download as real DICOM elements
 * - per-slice "slice shifts" are representable (even though the payload is the same per instance)
 *
 * Private tag layout (private group 2F47 per TREN / Craig spec):
 * - (2F47,0020) LO  PrivateCreator = "TREN_OHIF_REG" (reserves the 2000–20FF block; PS3.5 private pairing)
 * - (2F47,2000) UT  RegistrationLinkageJSON
 * - (2F47,2001) UT  RegistrationTranslationJSON
 *
 * Linkage JSON (v1) is canonicalized as:
 * {
 *   "schemaVersion": 1,
 *   "linkage": [{ "seriesInstanceUID", "seriesDescription?", "modality?" }, ...],
 *   "associateContour": "<SeriesInstanceUID of DE/SE contour source>"
 * }
 * The same object is stamped on every instance of each linked DWI-family series and on the
 * associate-contour series so all related series carry identical linkage metadata.
 */

export const TREN_PRIVATE_CREATOR = 'TREN_OHIF_REG';

export const TREN_TAG_PRIVATE_CREATOR = '2F470020';
export const TREN_TAG_LINKAGE_JSON = '2F472000';
export const TREN_TAG_TRANSLATIONS_JSON = '2F472001';

export const TREN_SCHEMA_VERSION = 1 as const;

export type TrenLinkageItem = {
  seriesInstanceUID: string;
  seriesDescription?: string;
  modality?: string;
};

export type TrenRegistrationLinkageV1 = {
  schemaVersion: typeof TREN_SCHEMA_VERSION;
  linkage: TrenLinkageItem[];
  /**
   * SeriesInstanceUID where the guide contour was drawn (DE/SE).
   * Named to match the product spec wording.
   */
  associateContour: string;
};

export type TrenRegistrationTranslationV1 = {
  schemaVersion: typeof TREN_SCHEMA_VERSION;
  /**
   * Translation in mm.
   * v1 stores *only translation* as requested.
   */
  translations_xyz_mm: [number, number, number];
};

type DicomJsonElement = {
  vr: string;
  Value?: unknown[];
};

export function trenEncodeJson(value: unknown): string {
  return JSON.stringify(value);
}

export function trenDecodeJson<T>(raw: unknown): T | undefined {
  try {
    if (!raw) {
      return;
    }

    // DICOM JSON element shape: { vr, Value: [ ... ] }
    if (typeof raw === 'object' && raw !== null && 'Value' in raw) {
      const element = raw as DicomJsonElement;
      const first = element?.Value?.[0];
      if (typeof first === 'string') {
        return JSON.parse(first) as T;
      }
      return;
    }

    if (typeof raw === 'string') {
      return JSON.parse(raw) as T;
    }
  } catch {
    // ignore malformed JSON
  }
}

export function trenMakeDicomJsonElement(vr: string, value: string): DicomJsonElement {
  return { vr, Value: [value] };
}

/**
 * Returns a metadata patch suitable for `DicomMetadataStore.updateMetadataForSeries`.
 * This patch is applied to *every instance* in the series.
 */
export function trenBuildInstanceTagPatch(params: {
  linkage?: TrenRegistrationLinkageV1;
  translations?: TrenRegistrationTranslationV1;
}): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    [TREN_TAG_PRIVATE_CREATOR]: trenMakeDicomJsonElement('LO', TREN_PRIVATE_CREATOR),
  };

  if (params.linkage) {
    patch[TREN_TAG_LINKAGE_JSON] = trenMakeDicomJsonElement('UT', trenEncodeJson(params.linkage));
  }

  if (params.translations) {
    patch[TREN_TAG_TRANSLATIONS_JSON] = trenMakeDicomJsonElement(
      'UT',
      trenEncodeJson(params.translations)
    );
  }

  return patch;
}

export function trenBuildLinkageV1(params: {
  linkage: TrenLinkageItem[];
  associateContourSeriesInstanceUID: string;
}): TrenRegistrationLinkageV1 {
  return {
    schemaVersion: TREN_SCHEMA_VERSION,
    linkage: params.linkage,
    associateContour: params.associateContourSeriesInstanceUID,
  };
}

export function trenBuildTranslationsV1(translations_xyz_mm: [number, number, number]) {
  return {
    schemaVersion: TREN_SCHEMA_VERSION,
    translations_xyz_mm,
  } satisfies TrenRegistrationTranslationV1;
}

export function trenGetLinkageFromInstance(
  instance: Record<string, unknown> | undefined
): TrenRegistrationLinkageV1 | undefined {
  if (!instance) {
    return;
  }
  return trenDecodeJson<TrenRegistrationLinkageV1>(instance[TREN_TAG_LINKAGE_JSON]);
}

export function trenGetTranslationsFromInstance(
  instance: Record<string, unknown> | undefined
): TrenRegistrationTranslationV1 | undefined {
  if (!instance) {
    return;
  }
  return trenDecodeJson<TrenRegistrationTranslationV1>(instance[TREN_TAG_TRANSLATIONS_JSON]);
}

export function isTrenSchemaV1(value: any): value is { schemaVersion: 1 } {
  return value?.schemaVersion === 1;
}

export type TrenRegistrationCommands = {
  setLinkage: (params: {
    studyInstanceUID: string;
    linkedSeries: TrenLinkageItem[];
    associateContourSeriesInstanceUID: string;
  }) => void;
  setTranslations: (params: {
    studyInstanceUID: string;
    linkedSeriesInstanceUIDs: string[];
    translations_xyz_mm: [number, number, number];
  }) => void;
};
